/**
 * The pinned bar's count copy, the pins list's `Pinned today by Dana` meta
 * line, and (RF-5) the files list's `Shared today` meta line, as pure,
 * tested functions.
 *
 * The pin body label (`pinBodyLabel`, ISSUE-1054) is also here, as a pure
 * function, so no pin kind renders a blank pinned bar or Pins row.
 *
 * One-line and two-line truncation is the CSS classes `line-clamp-1` and
 * `line-clamp-2`, applied at the call site. There is no JavaScript
 * truncation helper in this module, deliberately.
 */

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function shortDate(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(new Date(iso));
}

/**
 * `null` at zero pins and at exactly one pin, since a single pin shows only
 * the message text with no count. `${total} pinned messages` at two or more.
 */
export function pinnedBarLabel(total: number): string | null {
  if (total <= 1) return null;
  return `${total} pinned messages`;
}

export function pinnedMetaLine(
  pinnedAtIso: string,
  pinnedByDisplayName: string,
  now: Date,
): string {
  const pinnedAt = new Date(pinnedAtIso);
  if (isSameCalendarDay(pinnedAt, now)) {
    return `Pinned today by ${pinnedByDisplayName}`;
  }
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (isSameCalendarDay(pinnedAt, yesterday)) {
    return `Pinned yesterday by ${pinnedByDisplayName}`;
  }
  return `Pinned on ${shortDate(pinnedAtIso)} by ${pinnedByDisplayName}`;
}

export const PIN_LABEL_PHOTO = "Photo";
export const PIN_LABEL_GIF = "GIF";
export const PIN_LABEL_FILE = "File";
export const PIN_LABEL_POLL = "Poll";
export const PIN_LABEL_POLL_PREFIX = "Poll: ";
export const PIN_LABEL_GAME_CARD = "Game card";
export const PIN_LABEL_GAME_PREFIX = "Game: ";
export const PIN_LABEL_UNAVAILABLE = "Message unavailable";

export type typePinBodyLabelInput = {
  kind?: string | null;
  body?: string | null;
  pollQuestion?: string | null;
  gameCardLabel?: string | null;
};

function hasText(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim() !== "";
}

/**
 * The text a pin shows in the pinned bar and on its Pins row body line.
 * First match wins: a poll's question, a game card's matchup, any text body
 * (a text pin, an announcement, a captioned photo or GIF), then a label per
 * media kind, then `Message unavailable` for a pin that no longer resolves.
 * `kind` is a plain string so a kind added later never breaks this.
 */
export function pinBodyLabel(input: typePinBodyLabelInput): string {
  if (input.kind === "poll") {
    return hasText(input.pollQuestion)
      ? PIN_LABEL_POLL_PREFIX + input.pollQuestion.trim()
      : PIN_LABEL_POLL;
  }
  if (input.kind === "gameCard") {
    return hasText(input.gameCardLabel)
      ? PIN_LABEL_GAME_PREFIX + input.gameCardLabel.trim()
      : PIN_LABEL_GAME_CARD;
  }
  if (hasText(input.body)) return input.body;
  if (input.kind === "image") return PIN_LABEL_PHOTO;
  if (input.kind === "gif") return PIN_LABEL_GIF;
  if (input.kind === "pdf") return PIN_LABEL_FILE;
  return PIN_LABEL_UNAVAILABLE;
}

/**
 * RF-5's sibling formatter for the `Files` tab's row meta line: `Shared
 * today`, `Shared yesterday`, or `Shared on Aug 12`. Same calendar-day
 * comparison as `pinnedMetaLine`; `FilesTab.tsx` must not write a second
 * date helper of its own.
 */
export function fileMetaLine(sentAtIso: string, now: Date): string {
  const sentAt = new Date(sentAtIso);
  if (isSameCalendarDay(sentAt, now)) return "Shared today";
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (isSameCalendarDay(sentAt, yesterday)) return "Shared yesterday";
  return `Shared on ${shortDate(sentAtIso)}`;
}
